import { useState, useEffect } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { FileText, AlertTriangle, RefreshCw, Save } from 'lucide-react';

export const PONumberStartSetting = () => {
  const [startNumber, setStartNumber] = useState('');
  const [confirmationWord, setConfirmationWord] = useState('');
  const [currentSetting, setCurrentSetting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCurrentSetting();
  }, []);

  const loadCurrentSetting = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`${API}/settings/po-number-start`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentSetting(response.data);
      if (response.data.start_number) {
        setStartNumber(response.data.start_number);
      }
    } catch (error) {
      console.error('Error loading P.O. number setting:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!startNumber) {
      toast.error('Please enter a P.O. number start point');
      return;
    }

    if (!startNumber.startsWith('30X')) {
      toast.error('P.O. number must start with "30X"');
      return;
    }

    if (currentSetting?.is_set && confirmationWord.toUpperCase() !== 'OVERRIDE') {
      toast.error('To change the existing setting, type "OVERRIDE" in the confirmation field');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`${API}/settings/po-number-start`, {
        start_number: startNumber,
        confirmation_word: confirmationWord
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setConfirmationWord('');
      loadCurrentSetting();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2 p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
        <div className="animate-pulse h-6 bg-blue-200 rounded w-1/3"></div>
        <div className="animate-pulse h-4 bg-blue-200 rounded w-2/3"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
      <Label className="text-base font-semibold text-blue-900 flex items-center gap-2">
        <FileText className="w-5 h-5" />
        P.O. Expense Number Start Point
      </Label>
      <p className="text-xs text-blue-800">
        Set the starting point for P.O. expense numbers (30X****). Once set, numbers will continue consecutively.
      </p>
      
      {currentSetting?.is_set && (
        <div className="bg-white p-3 rounded-lg border border-blue-200 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Current Start:</span>
            <span className="font-bold text-blue-700 text-lg">{currentSetting.start_number}</span>
          </div>
          <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
            <span>Set by: {currentSetting.set_by}</span>
            <span>{new Date(currentSetting.set_at).toLocaleDateString()}</span>
          </div>
        </div>
      )}
      
      <div className="space-y-2">
        <Label htmlFor="po-start-number" className="text-sm text-blue-900">
          {currentSetting?.is_set ? 'New Start Number' : 'Start Number'}
        </Label>
        <Input
          id="po-start-number"
          value={startNumber}
          onChange={(e) => {
            let value = e.target.value.toUpperCase();
            if (value && !value.startsWith('30X')) {
              if (/^\d+$/.test(value)) {
                value = '30X' + value;
              }
            }
            setStartNumber(value);
          }}
          placeholder="30X8805"
          className="font-mono text-lg"
        />
        <p className="text-[10px] text-blue-600">Format: 30X followed by numbers (e.g., 30X8805)</p>
      </div>
      
      {currentSetting?.is_set && (
        <div className="space-y-2 p-3 bg-amber-50 rounded-lg border border-amber-300">
          <Label htmlFor="confirmation-word" className="text-sm text-amber-900 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            Confirmation Required
          </Label>
          <p className="text-[10px] text-amber-700">
            To change the existing P.O. start number, type <strong>OVERRIDE</strong> below
          </p>
          <Input
            id="confirmation-word"
            value={confirmationWord}
            onChange={(e) => setConfirmationWord(e.target.value)}
            placeholder="Type OVERRIDE to confirm"
            className="font-mono"
          />
        </div>
      )}
      
      <Button
        onClick={handleSave}
        disabled={saving || !startNumber}
        className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
      >
        {saving ? (
          <>
            <RefreshCw className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save className="w-4 h-4" />
            {currentSetting?.is_set ? 'Update Start Point' : 'Save Start Point'}
          </>
        )}
      </Button>
      
      {!currentSetting?.is_set && (
        <p className="text-[10px] text-blue-600 text-center">
          ⚠️ Once saved, this setting will require typing "OVERRIDE" to change
        </p>
      )}
    </div>
  );
};

export default PONumberStartSetting;
